import type { Notification } from '@repo/types/types/notification'
import { trackEvent } from '@/lib/analytics'
import { APP_CONFIG } from '@/lib/config/constants'
import { trpc } from '@/lib/trpc'
import { handleMutationError } from '@/lib/utils/errors'

// Polling-based notification client. This remains production-safe and can be upgraded
// to realtime transport later without changing the route/component contracts.

export function useNotifications() {
  const utils = trpc.useUtils()

  // Get all notifications with polling for real-time updates
  const { data, isLoading, error } = trpc.notifications.list.useQuery(
    { limit: 50, offset: 0 },
    {
      refetchInterval: APP_CONFIG.NOTIFICATION_POLL_INTERVAL,
      refetchOnWindowFocus: true,
    }
  )

  // Get unread count
  const { data: unreadData } = trpc.notifications.unreadCount.useQuery(undefined, {
    refetchInterval: APP_CONFIG.NOTIFICATION_POLL_INTERVAL,
    refetchOnWindowFocus: true,
  })

  // Mark as read mutation
  const markAsReadMutation = trpc.notifications.markAsRead.useMutation({
    onSuccess: (_result, variables) => {
      utils.notifications.list.invalidate()
      utils.notifications.unreadCount.invalidate()

      // Track notification read in PostHog
      const notification = data?.notifications.find((n) => n.id === variables.id)
      if (notification) {
        const timeToRead = notification.createdAt
          ? Math.floor((Date.now() - new Date(notification.createdAt).getTime()) / 1000)
          : undefined
        trackEvent('notification_read', {
          notification_id: notification.id,
          notification_type: notification.type,
          time_to_read_seconds: timeToRead,
        })
      }
    },
    onError: (error) => {
      handleMutationError(error, 'Failed to mark notification as read')
    },
  })

  // Mark as clicked mutation
  const markAsClickedMutation = trpc.notifications.markAsClicked.useMutation({
    onSuccess: (_result, variables) => {
      utils.notifications.list.invalidate()

      // Track notification clicked in PostHog
      const notification = data?.notifications.find((n) => n.id === variables.id)
      if (notification) {
        trackEvent('notification_clicked', {
          notification_id: notification.id,
          notification_type: notification.type,
          action_url: notification.actionUrl || undefined,
        })
      }
    },
    onError: (error) => {
      handleMutationError(error, 'Failed to mark notification as clicked')
    },
  })

  // Mark all as read mutation
  const markAllAsReadMutation = trpc.notifications.markAllAsRead.useMutation({
    onSuccess: () => {
      utils.notifications.list.invalidate()
      utils.notifications.unreadCount.invalidate()
    },
    onError: (error) => {
      handleMutationError(error, 'Failed to mark all notifications as read')
    },
  })

  // Create notification mutation
  const createMutation = trpc.notifications.create.useMutation({
    onSuccess: (result, variables) => {
      utils.notifications.list.invalidate()
      utils.notifications.unreadCount.invalidate()

      // Track notification sent in PostHog
      trackEvent('notification_sent', {
        notification_id: result.id,
        notification_type: variables.type,
        has_action: !!variables.actionUrl,
      })
    },
    onError: (error) => {
      handleMutationError(error, 'Failed to create notification')
    },
  })

  return {
    notifications: (data?.notifications ?? []) as Notification[],
    hasMore: data?.pageInfo?.hasMore || data?.hasMore || false,
    unreadCount: unreadData?.count || 0,
    isLoading,
    error,
    markAsRead: (id: string) => markAsReadMutation.mutate({ id }),
    markAsClicked: (id: string) => markAsClickedMutation.mutate({ id }),
    markAllAsRead: () => markAllAsReadMutation.mutate(),
    createNotification: createMutation.mutate,
  }
}
